import { useState, useRef, useEffect } from 'react'
import type { Product } from '../types'
import {
  WorkOrderStatuses,
  WorkOrderTypes,
  Priorities,
  type WorkOrder,
} from '@esf/shared'

// Simple icon renderer
const renderIcon = (path: string, className = 'w-5 h-5') => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
)

// Priority badge component
const PriorityBadge = ({ priority }: { priority: string }) => {
  const colors: Record<string, string> = {
    LOW: 'bg-gray-100 text-gray-700',
    MEDIUM: 'bg-info-100 text-info-700',
    HIGH: 'bg-warning-100 text-warning-700',
    CRITICAL: 'bg-error-100 text-error-700',
  }
  return (
    <span className={`badge ${colors[priority] || 'bg-gray-100 text-gray-700'}`}>
      {priority}
    </span>
  )
}

// Work Order Status badge component
const WorkOrderStatusBadge = ({ status }: { status: string }) => {
  const colors: Record<string, string> = {
    OPEN: 'bg-info-100 text-info-700',
    IN_PROGRESS: 'bg-warning-100 text-warning-700',
    COMPLETED: 'bg-success-100 text-success-700',
    CANCELLED: 'bg-gray-100 text-gray-500',
    ON_HOLD: 'bg-purple-100 text-purple-700',
  }
  return (
    <span className={`badge ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  )
}

interface WorkOrdersPageProps {
  products: Product[]
  workOrders: WorkOrder[]
  workOrderStatusFilter: string
  setWorkOrderStatusFilter: (v: string) => void
  workOrderPriorityFilter: string
  setWorkOrderPriorityFilter: (v: string) => void
  workOrderTypeFilter: string
  setWorkOrderTypeFilter: (v: string) => void
  workOrderAssetFilter: string
  setWorkOrderAssetFilter: (v: string) => void
  workOrderAssigneeFilter: string
  setWorkOrderAssigneeFilter: (v: string) => void
  workOrderSearchQuery: string
  setWorkOrderSearchQuery: (v: string) => void
  showDeleted: boolean
  setShowDeleted: (v: boolean) => void
  navigateTo: (page: string, id?: string) => void
  fetchWorkOrders: () => void
  setSelectedWorkOrder: (wo: WorkOrder) => void
  setWorkOrderDetailModalOpen: (v: boolean) => void
  onRestoreWorkOrder: (wo: WorkOrder) => void
}

export default function WorkOrdersPage({
  products,
  workOrders,
  workOrderStatusFilter,
  setWorkOrderStatusFilter,
  workOrderPriorityFilter,
  setWorkOrderPriorityFilter,
  workOrderTypeFilter,
  setWorkOrderTypeFilter,
  workOrderAssetFilter,
  setWorkOrderAssetFilter,
  workOrderAssigneeFilter,
  setWorkOrderAssigneeFilter,
  workOrderSearchQuery,
  setWorkOrderSearchQuery,
  showDeleted,
  setShowDeleted,
  navigateTo,
  fetchWorkOrders,
  setSelectedWorkOrder,
  setWorkOrderDetailModalOpen,
  onRestoreWorkOrder,
}: WorkOrdersPageProps) {
  const workOrderTypeLabels: Record<string, string> = {
    INSPECTION: 'Inspection',
    MAINTENANCE: 'Scheduled Maintenance',
    REPAIR: 'Repair',
    INSTALLATION: 'Installation',
    DECOMMISSION: 'Decommission',
    SENSOR_REPLACEMENT: 'Sensor Replacement',
  }

  // Local search state for immediate feedback
  const [searchInput, setSearchInput] = useState(workOrderSearchQuery)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Sync searchInput with workOrderSearchQuery when it changes externally
  useEffect(() => {
    setSearchInput(workOrderSearchQuery)
  }, [workOrderSearchQuery])

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      setWorkOrderSearchQuery(value)
    }, 300)
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  const getProductName = (assetId: string) => {
    const product = products.find(p => p.id === assetId)
    return product?.name || assetId
  }

  // Get unique assignees from work orders
  const uniqueAssignees = [...new Set(workOrders.map(wo => wo.assignedTo).filter(Boolean))] as string[]

  // Get unique assets that have work orders
  const assetsWithWorkOrders = [...new Set(workOrders.map(wo => wo.assetId))]
  const assetOptions = products.filter(p => assetsWithWorkOrders.includes(p.id))

  // Apply client-side filters
  const filteredWorkOrders = workOrders.filter(wo => {
    if (workOrderStatusFilter && wo.status !== workOrderStatusFilter) return false
    if (workOrderPriorityFilter && wo.priority !== workOrderPriorityFilter) return false
    if (workOrderTypeFilter && wo.type !== workOrderTypeFilter) return false
    if (workOrderAssetFilter && wo.assetId !== workOrderAssetFilter) return false
    if (workOrderAssigneeFilter && wo.assignedTo !== workOrderAssigneeFilter) return false
    if (workOrderSearchQuery) {
      const query = workOrderSearchQuery.toLowerCase()
      const matchesTitle = wo.title.toLowerCase().includes(query)
      const matchesDescription = wo.description?.toLowerCase().includes(query)
      const matchesAsset = getProductName(wo.assetId).toLowerCase().includes(query)
      if (!matchesTitle && !matchesDescription && !matchesAsset) return false
    }
    return true
  })

  // Calculate statistics
  const selectedAssetWorkOrderCount = workOrderAssetFilter
    ? workOrders.filter(wo => wo.assetId === workOrderAssetFilter).length
    : null
  const selectedAssigneeWorkOrderCount = workOrderAssigneeFilter
    ? workOrders.filter(wo => wo.assignedTo === workOrderAssigneeFilter).length
    : null

  const hasActiveFilters = workOrderStatusFilter || workOrderPriorityFilter || workOrderTypeFilter ||
    workOrderAssetFilter || workOrderAssigneeFilter || workOrderSearchQuery || showDeleted

  const clearAllFilters = () => {
    setWorkOrderStatusFilter('')
    setWorkOrderPriorityFilter('')
    setWorkOrderTypeFilter('')
    setWorkOrderAssetFilter('')
    setWorkOrderAssigneeFilter('')
    setSearchInput('')
    setWorkOrderSearchQuery('')
    setShowDeleted(false)
  }

  return (
    <div className="container-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Work Orders</h1>
        <button
          className="btn btn-primary"
          onClick={() => navigateTo('workorder-create')}
        >
          {renderIcon('M12 4v16m8-8H4')}
          Create Work Order
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search work orders by title, description, or asset..."
            value={searchInput}
            onChange={e => handleSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-esf-navy/20 focus:border-esf-navy"
          />
          {searchInput && (
            <button
              onClick={() => {
                setSearchInput('')
                setWorkOrderSearchQuery('')
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="filter-bar flex-wrap gap-2">
        <select
          className="filter-select"
          value={workOrderStatusFilter}
          onChange={e => setWorkOrderStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          {WorkOrderStatuses.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
        <select
          className="filter-select"
          value={workOrderPriorityFilter}
          onChange={e => setWorkOrderPriorityFilter(e.target.value)}
        >
          <option value="">All Priorities</option>
          {Priorities.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select
          className="filter-select"
          value={workOrderTypeFilter}
          onChange={e => setWorkOrderTypeFilter(e.target.value)}
        >
          <option value="">All Types</option>
          {WorkOrderTypes.map(t => <option key={t} value={t}>{workOrderTypeLabels[t] || t}</option>)}
        </select>
        <select
          className="filter-select"
          value={workOrderAssetFilter}
          onChange={e => setWorkOrderAssetFilter(e.target.value)}
        >
          <option value="">All Assets</option>
          {assetOptions.map(p => (
            <option key={p.id} value={p.id}>
              {p.name} ({workOrders.filter(wo => wo.assetId === p.id).length})
            </option>
          ))}
        </select>
        <select
          className="filter-select"
          value={workOrderAssigneeFilter}
          onChange={e => setWorkOrderAssigneeFilter(e.target.value)}
        >
          <option value="">All Assignees</option>
          {uniqueAssignees.map(assignee => (
            <option key={assignee} value={assignee}>
              {assignee} ({workOrders.filter(wo => wo.assignedTo === assignee).length})
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
            className="rounded border-gray-300"
          />
          Show deleted
        </label>
        <button onClick={fetchWorkOrders} className="btn btn-secondary" title="Refresh">
          {renderIcon('M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15')}
        </button>
        {hasActiveFilters && (
          <button onClick={clearAllFilters} className="btn btn-outline text-sm">
            Clear filters
          </button>
        )}
      </div>

      {/* Statistics Banner */}
      {(selectedAssetWorkOrderCount !== null || selectedAssigneeWorkOrderCount !== null) && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex flex-wrap gap-6">
            {selectedAssetWorkOrderCount !== null && (
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <span className="text-blue-800">
                  <strong>{getProductName(workOrderAssetFilter)}</strong> has{' '}
                  <strong>{selectedAssetWorkOrderCount}</strong> total work order{selectedAssetWorkOrderCount !== 1 ? 's' : ''}
                </span>
              </div>
            )}
            {selectedAssigneeWorkOrderCount !== null && (
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-blue-800">
                  <strong>{workOrderAssigneeFilter}</strong> has been assigned{' '}
                  <strong>{selectedAssigneeWorkOrderCount}</strong> work order{selectedAssigneeWorkOrderCount !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Results count */}
      {hasActiveFilters && (
        <div className="mt-4 text-sm text-gray-500">
          Showing {filteredWorkOrders.length} of {workOrders.length} work orders
        </div>
      )}

      {/* Work Orders Table */}
      <div className="table-card">
        <table className="table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Asset</th>
              <th>Type</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Assigned To</th>
              <th>Scheduled</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredWorkOrders.map(wo => (
              <tr key={wo.id} className={wo.deletedAt ? 'opacity-50 bg-gray-50' : ''}>
                <td className="font-medium">{wo.title}</td>
                <td className="text-sm text-gray-600">{getProductName(wo.assetId)}</td>
                <td className="text-sm">{workOrderTypeLabels[wo.type] || wo.type}</td>
                <td><PriorityBadge priority={wo.priority} /></td>
                <td>
                  {wo.deletedAt ? (
                    <span className="badge bg-red-100 text-red-700">Deleted</span>
                  ) : (
                    <WorkOrderStatusBadge status={wo.status} />
                  )}
                </td>
                <td className="text-sm text-gray-500">{wo.assignedTo || '-'}</td>
                <td className="text-sm text-gray-500">{formatDate(wo.scheduledDate)}</td>
                <td>
                  <div className="flex gap-1">
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={() => {
                        setSelectedWorkOrder(wo)
                        setWorkOrderDetailModalOpen(true)
                      }}
                    >
                      View
                    </button>
                    {wo.deletedAt ? (
                      <button
                        className="btn btn-sm bg-success-50 text-success-600 hover:bg-success-100"
                        onClick={() => onRestoreWorkOrder(wo)}
                        title="Restore work order"
                      >
                        Restore
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => navigateTo('workorder-edit', wo.id)}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filteredWorkOrders.length === 0 && (
              <tr>
                <td colSpan={8}>
                  <div className="empty-state">
                    <div className="empty-state-icon bg-warning-100 text-warning-600">
                      {renderIcon('M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4')}
                    </div>
                    <div className="empty-state-title">No work orders found</div>
                    <div className="empty-state-text">
                      {hasActiveFilters
                        ? 'Try adjusting your filters or search query'
                        : 'Create a work order to schedule maintenance tasks'}
                    </div>
                    {!hasActiveFilters && (
                      <button
                        className="btn btn-primary mt-4"
                        onClick={() => navigateTo('workorder-create')}
                      >
                        {renderIcon('M12 4v16m8-8H4')}
                        Create Work Order
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
